import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Loader2,
    Plus,
    FileText,
    Clock,
    CheckCircle2,
    EyeOff,
    Trash2,
    Download,
    Settings as SettingsIcon,
    ExternalLink,
    Save,
} from "lucide-react";
import { toast } from "sonner";
import { blogApi, BLOG_STATUSES } from "@/lib/blogApi";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
    draft: { icon: FileText, color: "text-slate-600 bg-slate-100", label: "Draft" },
    scheduled: { icon: Clock, color: "text-amber-700 bg-amber-100", label: "Scheduled" },
    published: {
        icon: CheckCircle2,
        color: "text-emerald-700 bg-emerald-100",
        label: "Published",
    },
    unpublished: { icon: EyeOff, color: "text-rose-700 bg-rose-100", label: "Unpublished" },
};

function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.draft;
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.72rem] font-medium ${meta.color}`}
            data-testid={`blog-row-status-${status}`}
        >
            <Icon size={11} />
            {meta.label}
        </span>
    );
}

export default function AdminBlog() {
    const navigate = useNavigate();
    const [posts, setPosts] = useState(null);
    const [filter, setFilter] = useState("");
    const [query, setQuery] = useState("");
    const [settings, setSettings] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);

    const load = () => {
        setPosts(null);
        blogApi
            .list(filter || undefined)
            .then(setPosts)
            .catch((err) => {
                console.error(err);
                toast.error("Could not load posts");
                setPosts([]);
            });
    };

    useEffect(() => {
        load();
        blogApi
            .getSettings()
            .then(setSettings)
            .catch(() => setSettings({ default_disclaimer: "" }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const filtered = useMemo(() => {
        if (!posts) return [];
        if (!query.trim()) return posts;
        const q = query.toLowerCase();
        return posts.filter(
            (p) =>
                p.title.toLowerCase().includes(q) ||
                p.slug.toLowerCase().includes(q) ||
                (p.category || "").toLowerCase().includes(q)
        );
    }, [posts, query]);

    const act = async (fn, successMsg) => {
        try {
            await fn();
            toast.success(successMsg);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Action failed");
        }
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const updated = await blogApi.updateSettings({
                default_disclaimer: settings?.default_disclaimer || "",
            });
            setSettings(updated);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(err?.message || "Could not save settings");
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-blog-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="mm-eyebrow">Content</div>
                    <h1 className="font-serif text-3xl text-mm-navy">Blog manager</h1>
                    <p className="mt-1 max-w-xl text-sm text-mm-muted">
                        Create, approve, and schedule blog posts. API-created drafts
                        appear here with status <strong>draft</strong>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/admin/blog/import"
                        className="mm-btn-outline !px-4 !py-2 text-sm"
                        data-testid="admin-blog-import-btn"
                    >
                        <Download size={14} />
                        Import JSON
                    </Link>
                    <Link
                        to="/admin/blog/new"
                        className="mm-btn-primary !px-4 !py-2 text-sm"
                        data-testid="admin-blog-new-btn"
                    >
                        <Plus size={14} />
                        New post
                    </Link>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-mm-line bg-white p-4">
                <div className="min-w-[180px]">
                    <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
                        <SelectTrigger data-testid="admin-blog-filter-status">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {BLOG_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {STATUS_META[s]?.label || s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Input
                    placeholder="Search title, slug, category..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="max-w-sm"
                    data-testid="admin-blog-search"
                />
                <div className="ml-auto text-xs text-mm-muted">
                    {posts ? `${filtered.length} / ${posts.length}` : "Loading..."}
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-mm-line bg-white">
                {posts === null ? (
                    <div className="flex items-center justify-center gap-2 p-10 text-sm text-mm-muted">
                        <Loader2 className="animate-spin" size={16} /> Loading posts...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-mm-muted" data-testid="admin-blog-empty">
                        No posts yet. Click <strong>New post</strong> or <strong>Import JSON</strong> to add one.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg/50 text-left text-[0.72rem] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Category</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Publish at</th>
                                <th className="px-4 py-3">Updated</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr
                                    key={p.id}
                                    className="border-t border-mm-line hover:bg-mm-bg/40"
                                    data-testid={`admin-blog-row-${p.slug}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-mm-ink">{p.title}</div>
                                        <div className="text-xs text-mm-muted">/blog/{p.slug}</div>
                                    </td>
                                    <td className="px-4 py-3 text-mm-muted">{p.category}</td>
                                    <td className="px-4 py-3">
                                        <StatusChip status={p.status} />
                                    </td>
                                    <td className="px-4 py-3 text-xs text-mm-muted">
                                        {p.publish_at ? p.publish_at.replace("T", " ").slice(0, 16) + " UTC" : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-mm-muted">
                                        {p.updated_at ? p.updated_at.replace("T", " ").slice(0, 16) + " UTC" : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                            <button
                                                onClick={() => navigate(`/admin/blog/${p.id}/edit`)}
                                                className="rounded-full border border-mm-line px-3 py-1 text-xs font-medium text-mm-navy hover:bg-mm-bg"
                                                data-testid={`admin-blog-edit-${p.slug}`}
                                            >
                                                Edit
                                            </button>
                                            <a
                                                href={`/blog/${p.slug}${p.status === "published" ? "" : "?preview=1"}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 rounded-full border border-mm-line px-3 py-1 text-xs font-medium text-mm-navy hover:bg-mm-bg"
                                                data-testid={`admin-blog-view-${p.slug}`}
                                            >
                                                <ExternalLink size={11} />
                                                View
                                            </a>
                                            {p.status === "draft" && (
                                                <button
                                                    onClick={() =>
                                                        act(() => blogApi.approve(p.id), "Approved and scheduled")
                                                    }
                                                    className="rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
                                                    data-testid={`admin-blog-approve-${p.slug}`}
                                                >
                                                    Approve
                                                </button>
                                            )}
                                            {p.status !== "published" && (
                                                <button
                                                    onClick={() =>
                                                        act(() => blogApi.publishNow(p.id), "Published now")
                                                    }
                                                    className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                                                    data-testid={`admin-blog-publish-${p.slug}`}
                                                >
                                                    Publish now
                                                </button>
                                            )}
                                            {p.status === "published" && (
                                                <button
                                                    onClick={() =>
                                                        act(() => blogApi.unpublish(p.id), "Unpublished")
                                                    }
                                                    className="rounded-full border border-mm-line px-3 py-1 text-xs font-medium text-mm-navy hover:bg-mm-bg"
                                                    data-testid={`admin-blog-unpublish-${p.slug}`}
                                                >
                                                    Unpublish
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    if (window.confirm(`Delete "${p.title}"? Soft-delete only.`))
                                                        act(() => blogApi.remove(p.id), "Deleted");
                                                }}
                                                className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                                                data-testid={`admin-blog-delete-${p.slug}`}
                                            >
                                                <Trash2 size={11} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="rounded-xl border border-mm-line bg-white p-5" data-testid="admin-blog-settings">
                <div className="mb-3 flex items-center gap-2">
                    <SettingsIcon size={14} className="text-mm-muted" />
                    <h3 className="text-sm font-semibold text-mm-navy">Default disclaimer</h3>
                </div>
                <p className="mb-3 text-xs text-mm-muted">
                    Applied to any post that doesn't set its own <code>disclaimer</code>.
                </p>
                <textarea
                    className="h-28 w-full rounded-lg border border-mm-line bg-mm-bg/40 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky"
                    value={settings?.default_disclaimer || ""}
                    onChange={(e) =>
                        setSettings((s) => ({ ...(s || {}), default_disclaimer: e.target.value }))
                    }
                    data-testid="admin-blog-settings-disclaimer"
                />
                <div className="mt-3 flex justify-end">
                    <button
                        onClick={saveSettings}
                        disabled={savingSettings}
                        className="mm-btn-primary !px-4 !py-2 text-sm"
                        data-testid="admin-blog-settings-save"
                    >
                        {savingSettings ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
